import { useCallback, useEffect, useRef, useState } from "react";

interface WakewordManagerProps {
  onWakewordDetected: () => void;
  onSpeechStart?: () => void;
}

export const useWakewordManager = ({
  onWakewordDetected,
  onSpeechStart,
}: WakewordManagerProps) => {
  const [isWakewordEnabled, setIsWakewordEnabled] = useState<boolean>(false);
  const [wakewordDetected, setWakewordDetected] = useState<boolean>(false);
  const [isListening, setIsListening] = useState<boolean>(false);
  const [isLoadingModels, setIsLoadingModels] = useState<boolean>(true);

  const wakewordRef = useRef<any>(null);
  const isListeningRef = useRef<boolean>(false);

  // ---------- INIT WAKEWORD (with integrated VAD) ----------
  const initializeWakeword = useCallback(async () => {
    try {
      setIsLoadingModels(true);
      
      if (!window.VETCOIWakeWord) {
        console.error("VETCOIWakeWord not available");
        setIsLoadingModels(false);
        return;
      }

      const options = {
        debug: false,
        vadModelPath: "/wakeword/models/silero-vad.onnx",
        spectrogramModelPath: "/wakeword/models/mel-spectrogram.onnx",
        vetcoiModelPath: "/wakeword/models/vetcoi.onnx",
        vetcoiThreshold: 0.995,
        vetcoiVerificationThreshold: 0.995,
      };

      wakewordRef.current = new window.VETCOIWakeWord(options);

      // Wait for all models to be loaded
      // VETCOI model has waitUntilLoaded method
      if (wakewordRef.current.vetcoi?.waitUntilLoaded) {
        await wakewordRef.current.vetcoi.waitUntilLoaded();
      }
      
      // Wait for VAD model if it has waitUntilLoaded
      if (wakewordRef.current.vad?.waitUntilLoaded) {
        await wakewordRef.current.vad.waitUntilLoaded();
      }
      
      // Wait for spectrogram model if it has waitUntilLoaded
      if (wakewordRef.current.spectrogram?.waitUntilLoaded) {
        await wakewordRef.current.spectrogram.waitUntilLoaded();
      }

      // Additional wait to ensure all models are fully initialized
      // The test() methods are called in constructor, so we wait a bit more
      await new Promise(resolve => setTimeout(resolve, 100));

      // Add wakeword detection callbacks
      wakewordRef.current.onVerified("vetcoi", (result: any) => {
        setWakewordDetected(result.verified);

        if (result.verified) {
          setIsListening(true);
          isListeningRef.current = true;
          onWakewordDetected();
        }
      });

      wakewordRef.current.onSpeechStart(() => {
        if (isListeningRef.current) {
          onSpeechStart?.();
        }
      });

      setIsWakewordEnabled(true);
      setIsLoadingModels(false);
    } catch (error) {
      console.error("Wakeword initialization error:", error);
      setIsLoadingModels(false);
    }
  }, [onWakewordDetected, onSpeechStart]);

  // ---------- START VAD (using VETCOI's integrated VAD) ----------
  const startVAD = useCallback(async () => {
    if (wakewordRef.current) {}
  }, []);

  // ---------- STOP VAD (using VETCOI's integrated VAD) ----------
  const stopVAD = useCallback(async () => {
    if (wakewordRef.current) {}
  }, []);

  // ---------- START LISTENING ----------
  const startListening = useCallback(async () => {
    setIsListening(true);
    isListeningRef.current = true;
    await startVAD();
  }, [startVAD]);

  // ---------- STOP LISTENING ----------
  const stopListening = useCallback(async () => {
    setIsListening(false);
    isListeningRef.current = false;
    await stopVAD();
  }, [stopVAD]);

  // Wakeword disabled: the /wakeword/models/*.onnx assets return 502 on this
  // deploy and the feature is unused (the mic streams continuously and the
  // server owns VAD/turn-taking). Skip init entirely — no ONNX fetches, no
  // console errors. Mark models "loaded" so the init gate (isInitializing in
  // the page) clears. initializeWakeword() is kept for reference but not called.
  void initializeWakeword;
  useEffect(() => {
    setIsLoadingModels(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wakewordRef.current) {
        // VETCOI system cleanup is handled internally
        setIsWakewordEnabled(false);
        setWakewordDetected(false);
      }
    };
  }, []);

  return {
    isWakewordEnabled,
    wakewordDetected,
    isListening,
    isListeningRef,
    startListening,
    stopListening,
    startVAD,
    stopVAD,
    isLoadingModels,
  };
};
